/**
 * useEntitlement (T64). Reads /api/v1/me/entitlements once per session
 * (10-minute staleTime so the FE doesn't hammer the BE on every gate
 * check) and returns the row for `code`, or undefined if not present.
 *
 * UX semantics:
 * - `enabled` → render the real feature surface.
 * - falsey value → render the Pro upgrade CTA / disable the action.
 *
 * Backend enforcement is independent — every Pro endpoint still gates via
 * @RequiresEntitlement and returns 402 Payment Required to unentitled
 * tenants regardless of what the FE believes.
 */
import { useQuery } from "@tanstack/react-query";
import { entitlementsApi, entitlementsQueryKeys, type EntitlementRow } from "./api";

const STALE_MS = 10 * 60 * 1000; // 10 minutes
/**
 * Mã quyền lợi chỉ gói trả phí mới có — dùng khi KHÔNG hỏi được máy chủ.
 *
 * Phải là đúng các mã máy chủ phát (`EntitlementCode.java`). Danh sách cũ ở đây dùng những mã máy
 * chủ chưa bao giờ phát (`module.e-invoice`, `module.gov-rx`…), nên khi API lỗi thì mọi tính năng
 * Pro thật — mã giảm giá, hạng thành viên — rơi vào nhánh "coi như được phép": hỏng theo hướng mở.
 * `WebEntitlementCodesTest` (arch-tests) ghim từng mã ở đây với file Java.
 */
export const PRO_DENY_FALLBACK = new Set([
  "module.multi-branch",
  "module.bhyt-export",
  "module.ai-assistant",
  "module.advanced-reports",
  "module.gov-eprescription",
  "module.einvoice",
  "module.plugins",
  "module.coupon",
  "module.membership",
]);

export interface UseEntitlementResult {
  /** True when the tenant holds the entitlement (BE-confirmed). */
  enabled: boolean;
  /** Full row for the code, or null if not on the BE response. */
  row: EntitlementRow | null;
  /** Loading state — gates that want to defer rendering should check this. */
  isLoading: boolean;
  /** True when the BE call failed; we fall back to a Pro deny-list. */
  isError: boolean;
}

export function useEntitlement(code: string): UseEntitlementResult {
  const query = useQuery({
    queryKey: entitlementsQueryKeys.mine(),
    queryFn: () => entitlementsApi.mine(),
    staleTime: STALE_MS,
  });

  if (query.isLoading) {
    return { enabled: false, row: null, isLoading: true, isError: false };
  }
  if (query.isError || !query.data) {
    // Fail safe: treat known-Pro codes as locked, everything else as enabled
    // (so a stale BE doesn't lock a clinic out of Lite features they paid for).
    return {
      enabled: !PRO_DENY_FALLBACK.has(code),
      row: null,
      isLoading: false,
      isError: true,
    };
  }
  const row = query.data.entitlements.find((r) => r.code === code) ?? null;
  return {
    enabled: row?.enabled ?? false,
    row,
    isLoading: false,
    isError: false,
  };
}
